import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  UploadCloud,
  FileStack,
  Shuffle,
  ScanSearch,
  PenLine,
  Lock,
  Square,
  Crop,
} from 'lucide-react'
import { useProjectStore } from '../store/projectStore.js'
import { buildDocumentFromFile } from '../lib/pdfActions.js'

function FeatureItem({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-white/70 p-3 ring-1 ring-slate-200">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
    </div>
  )
}

export default function EmptyState() {
  const addDocument = useProjectStore((s) => s.addDocument)
  const setProcessing = useProjectStore((s) => s.setProcessing)
  const showToast = useProjectStore((s) => s.showToast)

  const onDrop = useCallback(
    async (files) => {
      setProcessing(true, `Reading ${files.length} file${files.length === 1 ? '' : 's'}...`)
      try {
        for (const file of files) {
          try {
            const doc = await buildDocumentFromFile(file)
            addDocument(doc)
          } catch (err) {
            showToast(err.message, 'error')
          }
        }
      } finally {
        setProcessing(false)
      }
    },
    [addDocument, setProcessing, showToast]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    noClick: true,
    multiple: true,
  })

  return (
    <div className="flex h-full w-full items-center justify-center overflow-y-auto bg-slate-50 p-4 sm:p-8">
      <div className="w-full max-w-2xl">
        <div
          {...getRootProps()}
          className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed bg-white p-6 text-center shadow-soft transition-colors sm:p-12 ${
            isDragActive
              ? 'border-brand-500 bg-brand-50/60'
              : 'border-slate-300 hover:border-brand-400'
          }`}
        >
          <input {...getInputProps()} />
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-100 text-brand-700">
            <UploadCloud className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            Drop PDFs to get started
          </h2>
          <p className="max-w-md text-sm text-slate-500">
            Upload one or more PDF files to merge, reorder pages, exclude what you
            don't need, fill forms, or stamp custom text — everything happens
            locally in your browser.
          </p>
          <button onClick={open} type="button" className="btn-primary mt-2">
            <UploadCloud className="h-4 w-4" />
            Choose files
          </button>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500">
            <Lock className="h-3.5 w-3.5" />
            Files never leave your device.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
          <FeatureItem
            icon={FileStack}
            title="Multi-file merge"
            description="Combine pages across multiple PDFs into one clean output."
          />
          <FeatureItem
            icon={Shuffle}
            title="Reorder, rotate, exclude"
            description="Drag thumbnails to reorder. Rotate or hide individual pages."
          />
          <FeatureItem
            icon={Square}
            title="Permanent redaction"
            description="Drag rectangles to burn opaque black blocks into export."
          />
          <FeatureItem
            icon={Crop}
            title="Crop & sanitize"
            description="Set per-page crop boxes and strip metadata on export."
          />
          <FeatureItem
            icon={ScanSearch}
            title="OCR + form fill"
            description="Detect AcroForm fields. Run Tesseract to embed searchable text."
          />
          <FeatureItem
            icon={PenLine}
            title="Centered text stamps"
            description="Click to drop a stamp that's perfectly centered on the click point."
          />
        </div>
      </div>
    </div>
  )
}
